// src/pages/AboutPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useThemePalette } from '../hooks/useThemePalette';
import { useThemeStore }   from '../stores/useThemeStore';

// ── Shared icon props ─────────────────────────────────────────────────────────
const SI = { fill: 'none', stroke: 'currentColor', strokeWidth: '1.9', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcoTarget   = () => <svg width="14" height="14" viewBox="0 0 24 24" {...SI}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
const IcoRocket   = () => <svg width="14" height="14" viewBox="0 0 24 24" {...SI}><path d="M4.5 16.5c-1.5 1.5-1.5 3.5 0 5s3.5 1.5 5 0l10-10c3-3 3-8 0-10-2-1-5-1-7 2z"/><path d="M8 16l-5 5M17 7l1-1"/></svg>;
const IcoFlag     = () => <svg width="14" height="14" viewBox="0 0 24 24" {...SI}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;
const IcoHeart    = () => <svg width="14" height="14" viewBox="0 0 24 24" {...SI}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const IcoUsers    = () => <svg width="14" height="14" viewBox="0 0 24 24" {...SI}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;

// ── Value card icons (20px) ───────────────────────────────────────────────────
const IcoGradCap  = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>;
const IcoGlobe20  = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;
const IcoUsers20  = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoBulb20   = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>;
const IcoLink20   = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;
const IcoTrend20  = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;

// ── Sport badge icons ─────────────────────────────────────────────────────────
const SportSoccer  = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 0-6.9 17.2M12 22a10 10 0 0 0 6.9-17.2"/><path d="M9 8l3 2 3-2"/><path d="M9 16l3-2 3 2"/></svg>;
const SportRun     = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><circle cx="13" cy="4" r="2"/><path d="M7 22l3-7 3 3 4-8"/><path d="M9 12H5"/><path d="M16 10l3-2"/></svg>;
const SportSwim    = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><path d="M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M2 17c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><circle cx="16" cy="6" r="2"/><path d="M14 8l-4 4"/></svg>;
const SportTrophy  = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><polyline points="14.5 17 17 17 17 21 7 21 7 17 9.5 17"/><path d="M17 7V3H7v4a5 5 0 0 0 10 0z"/><path d="M7 7H5a3 3 0 0 0 3 3"/><path d="M17 7h2a3 3 0 0 1-3 3"/></svg>;
const SportWhistle = () => <svg width="20" height="20" viewBox="0 0 24 24" {...SI}><circle cx="7" cy="12" r="4"/><path d="M11 12h4l4-4"/><path d="M19 8l2-2"/><path d="M7 8v0"/></svg>;

// ── Accent purple (matching HomePage) ────────────────────────────────────────
const PURPLE = '#4f3ff5';

// ── Keyframe injection ────────────────────────────────────────────────────────
(function injectAboutStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-about-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes abRevealUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes abRevealScale {
      from { opacity: 0; transform: scale(0.93) translateY(14px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);    }
    }
    @keyframes abFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes abMeshFloat1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33%      { transform: translate(-50px, 60px) scale(1.07); }
      66%      { transform: translate(35px, -44px) scale(0.94); }
    }
    @keyframes abMeshFloat2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      40%      { transform: translate(60px, -55px) scale(1.10); }
      70%      { transform: translate(-28px, 38px) scale(0.93); }
    }
    @keyframes abMeshFloat3 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50%      { transform: translate(-42px, -30px) scale(1.05); }
    }
    @keyframes abParticleDrift {
      0%   { transform: translate(0, 0) scale(1);   opacity: var(--ab-p-opacity); }
      33%  { transform: translate(var(--ab-px), var(--ab-py)) scale(1.4); opacity: calc(var(--ab-p-opacity) * 1.6); }
      66%  { transform: translate(calc(var(--ab-px) * -0.5), calc(var(--ab-py) * 1.3)) scale(0.8); opacity: calc(var(--ab-p-opacity) * 0.6); }
      100% { transform: translate(0, 0) scale(1);   opacity: var(--ab-p-opacity); }
    }
    @keyframes abPulseGlow {
      0%, 100% { opacity: 0.50; }
      50%      { opacity: 1.00; }
    }
    @keyframes abStatFloat {
      0%, 100% { transform: translateY(0px); }
      50%      { transform: translateY(-6px); }
    }
    @keyframes abShimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes abSpin {
      to { transform: rotate(360deg); }
    }
    @keyframes abOrbitRing {
      from { transform: translate(-50%, -50%) rotate(-18deg); }
      to   { transform: translate(-50%, -50%) rotate(342deg); }
    }
    @keyframes abBorderGlow {
      0%, 100% { opacity: 0.6; }
      50%      { opacity: 1.0; }
    }
    @keyframes abBadgeFloat {
      0%, 100% { transform: translateY(0px) rotate(var(--badge-rot, 0deg)); }
      50%      { transform: translateY(-6px) rotate(var(--badge-rot, 0deg)); }
    }
    @keyframes abEcPulse1 {
      0%   { stroke-dashoffset:  180; opacity: 0;   }
      6%   { opacity: 0.8; }
      82%  { opacity: 0.8; }
      92%  { stroke-dashoffset: -980; opacity: 0;   }
      100% { stroke-dashoffset: -980; opacity: 0;   }
    }
    @keyframes abEcPulse2 {
      0%   { stroke-dashoffset:  140; opacity: 0;   }
      8%   { opacity: 0.6; }
      84%  { opacity: 0.6; }
      94%  { stroke-dashoffset: -920; opacity: 0;   }
      100% { stroke-dashoffset: -920; opacity: 0;   }
    }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
  document.head.appendChild(s);
})();

// ── Scroll-reveal hook ────────────────────────────────────────────────────────
function useReveal(threshold = 0.10) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); ob.disconnect(); } },
      { threshold },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [threshold]);
  return [ref, inView] as const;
}

function revealStyle(visible: boolean, delay = 0): React.CSSProperties {
  return {
    opacity:    visible ? 1 : 0,
    transform:  visible ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
  };
}

// ── Particle backdrop ─────────────────────────────────────────────────────────
function AboutParticleField({ isDark }: { isDark: boolean }) {
  const particles = React.useMemo(() => {
    const rng = (min: number, max: number) => min + Math.random() * (max - min);
    return Array.from({ length: 22 }, (_, i) => ({
      id: i, left: `${rng(0, 100)}%`, top: `${rng(0, 100)}%`,
      size: rng(1, 3),
      opacity: rng(0.05, 0.22),
      dur:   `${rng(9, 24)}s`,
      delay: `${rng(0, 14)}s`,
      px:    `${rng(-28, 28)}px`,
      py:    `${rng(-28, 28)}px`,
    }));
  }, []);
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position:     'absolute',
            left:         p.left,
            top:          p.top,
            width:        `${p.size}px`,
            height:       `${p.size}px`,
            borderRadius: '50%',
            background:   isDark
              ? `radial-gradient(circle, rgba(168,133,247,0.9), rgba(79,140,247,0.5))`
              : `radial-gradient(circle, rgba(79,63,245,0.7), rgba(79,140,247,0.4))`,
            boxShadow:    isDark ? `0 0 ${p.size * 3}px rgba(168,133,247,0.6)` : 'none',
            ['--ab-p-opacity' as never]: p.opacity,
            ['--ab-px' as never]: p.px,
            ['--ab-py' as never]: p.py,
            animation:    `abParticleDrift ${p.dur} ease-in-out ${p.delay} infinite`,
            opacity:      p.opacity,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

// ── Ambient gradient mesh blob ────────────────────────────────────────────────
function MeshBlob({
  color, size, pos, anim, opacity = 1,
}: {
  color: string; size: string; pos: React.CSSProperties;
  anim: string; opacity?: number;
}) {
  return (
    <div
      aria-hidden="true"
      style={{
        position:     'absolute',
        ...pos,
        width:        size,
        height:       size,
        borderRadius: '50%',
        background:   color,
        filter:       'blur(72px)',
        opacity,
        animation:    anim,
        pointerEvents:'none',
        zIndex:       0,
      }}
    />
  );
}

// ── Header energy curve ───────────────────────────────────────────────────────
function HeaderEnergyCurve({ C }: { C: ReturnType<typeof useThemePalette> }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 320"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, overflow: 'visible' }}
    >
      <defs>
        <linearGradient id="ab-ec-g1" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={C.accent}  stopOpacity="0" />
          <stop offset="35%"  stopColor={C.accent}  stopOpacity="0.85" />
          <stop offset="65%"  stopColor={C.accent2} stopOpacity="1" />
          <stop offset="100%" stopColor={PURPLE}     stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ab-ec-g2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%"   stopColor={PURPLE}    stopOpacity="0" />
          <stop offset="45%"  stopColor={C.accent2} stopOpacity="0.75" />
          <stop offset="100%" stopColor={C.accent}  stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Soft glow layer */}
      <path d="M -20 260 C 150 60, 320 300, 480 140 S 660 20, 820 180"
        fill="none" stroke="url(#ab-ec-g1)" strokeWidth="20" strokeLinecap="round"
        strokeDasharray="160 9999" opacity="0.22"
        style={{ animation: 'abEcPulse1 9s ease-in-out infinite', willChange: 'stroke-dashoffset, opacity' }} />
      {/* Bright core */}
      <path d="M -20 260 C 150 60, 320 300, 480 140 S 660 20, 820 180"
        fill="none" stroke="url(#ab-ec-g1)" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="160 9999"
        style={{ animation: 'abEcPulse1 9s ease-in-out infinite', willChange: 'stroke-dashoffset, opacity' }} />
      <path d="M 820 60 C 620 240, 440 20, 280 180 S 80 320, -20 140"
        fill="none" stroke="url(#ab-ec-g2)" strokeWidth="14" strokeLinecap="round"
        strokeDasharray="120 9999" opacity="0.20"
        style={{ animation: 'abEcPulse2 12s ease-in-out 3s infinite', willChange: 'stroke-dashoffset, opacity' }} />
      <path d="M 820 60 C 620 240, 440 20, 280 180 S 80 320, -20 140"
        fill="none" stroke="url(#ab-ec-g2)" strokeWidth="1.5" strokeLinecap="round"
        strokeDasharray="120 9999"
        style={{ animation: 'abEcPulse2 12s ease-in-out 3s infinite', willChange: 'stroke-dashoffset, opacity' }} />
    </svg>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function SectionBadge({ icon, label, C, isDark }: {
  icon: React.ReactNode; label: string;
  C: ReturnType<typeof useThemePalette>; isDark: boolean;
}) {
  return (
    <span style={{
      display:        'inline-flex',
      alignItems:     'center',
      gap:            '6px',
      background:     isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.08)',
      color:          C.accent,
      border:         `1px solid ${isDark ? 'rgba(79,140,247,0.30)' : 'rgba(79,140,247,0.22)'}`,
      borderRadius:   '50px',
      padding:        '5px 14px',
      fontSize:       '0.75rem',
      fontWeight:     700,
      letterSpacing:  '0.09em',
      textTransform:  'uppercase',
      marginBottom:   '14px',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      animation:      'abFadeIn 0.5s ease both',
    }}>
      {icon} {label}
    </span>
  );
}

function GradientTitle({ children, C }: { children: React.ReactNode; C: ReturnType<typeof useThemePalette> }) {
  return (
    <h2 style={{
      fontSize:             'clamp(1.85rem, 4vw, 2.6rem)',
      fontWeight:           900,
      lineHeight:           1.2,
      margin:               '0 0 14px',
      background:           `linear-gradient(135deg, ${C.text} 0%, ${C.accent} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor:  'transparent',
      backgroundClip:       'text',
      letterSpacing:        '-0.02em',
    }}>
      {children}
    </h2>
  );
}

// ── Glowing orbit ring for team cards ────────────────────────────────────────
function InitialsOrbit({ initials, C, isDark }: {
  initials: string; C: ReturnType<typeof useThemePalette>; isDark: boolean;
}) {
  return (
    <div style={{ position: 'relative', width: '92px', height: '92px', margin: '0 auto 16px' }}>
      {/* Orbit ring */}
      <div style={{
        position:     'absolute',
        top:          '50%',
        left:         '50%',
        width:        '108px',
        height:       '108px',
        borderRadius: '50%',
        border:       `1px solid ${isDark ? 'rgba(124,111,247,0.30)' : 'rgba(79,63,245,0.20)'}`,
        transform:    'translate(-50%, -50%) rotate(-18deg)',
        animation:    'abOrbitRing 14s linear infinite',
        pointerEvents:'none',
      }}>
        <div style={{
          position:     'absolute',
          top:          '-4px',
          left:         '50%',
          transform:    'translateX(-50%)',
          width:        '8px',
          height:       '8px',
          borderRadius: '50%',
          background:   C.accent,
          boxShadow:    `0 0 10px ${C.accent}, 0 0 20px ${C.accent}`,
        }} />
      </div>
      {/* Avatar */}
      <div style={{
        position:       'absolute',
        top: '50%', left: '50%',
        transform:      'translate(-50%, -50%)',
        width:          '88px',
        height:         '88px',
        borderRadius:   '50%',
        background:     isDark
          ? 'rgba(255,255,255,0.10)'
          : 'rgba(255,255,255,0.30)',
        border:         `2px solid rgba(255,255,255,0.35)`,
        backdropFilter: 'blur(8px)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        fontSize:       '1.35rem',
        fontWeight:     800,
        color:          '#fff',
        letterSpacing:  '-0.01em',
        boxShadow:      `0 0 0 4px rgba(79,63,245,0.18), 0 8px 28px rgba(0,0,0,0.35)`,
      }}>
        {initials}
      </div>
    </div>
  );
}

// ── Mission objective row ─────────────────────────────────────────────────────
function MissionRow({ idx, text, C, isDark, visible, delay }: {
  idx: number; text: string;
  C: ReturnType<typeof useThemePalette>; isDark: boolean;
  visible: boolean; delay: number;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <li
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:      'flex',
        alignItems:   'flex-start',
        gap:          '16px',
        padding:      '14px 18px',
        borderRadius: '12px',
        background:   hovered
          ? (isDark ? 'rgba(79,140,247,0.07)' : 'rgba(79,140,247,0.05)')
          : 'transparent',
        border:       `1px solid ${hovered
          ? (isDark ? 'rgba(79,140,247,0.22)' : 'rgba(79,140,247,0.18)')
          : 'transparent'}`,
        transition:   'background 0.22s ease, border-color 0.22s ease, transform 0.22s ease',
        transform:    hovered ? 'translateX(4px)' : 'translateX(0)',
        cursor:       'default',
        listStyle:    'none',
        ...revealStyle(visible, delay),
      }}
    >
      <div style={{
        width:           '28px',
        height:          '28px',
        borderRadius:    '8px',
        background:      hovered
          ? `linear-gradient(135deg, ${C.accent}, ${C.accent2})`
          : (isDark ? 'rgba(79,140,247,0.15)' : 'rgba(79,140,247,0.10)'),
        border:          `1px solid ${isDark ? 'rgba(79,140,247,0.30)' : 'rgba(79,140,247,0.20)'}`,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        flexShrink:      0,
        marginTop:       '1px',
        transition:      'background 0.22s ease',
        backdropFilter:  'blur(4px)',
      }}>
        <span style={{
          fontSize:  '0.66rem',
          fontWeight:900,
          color:     hovered ? '#fff' : C.accent,
          transition:'color 0.22s ease',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {String(idx + 1).padStart(2, '0')}
        </span>
      </div>
      <span style={{
        color:      C.muted,
        fontSize:   '0.97rem',
        lineHeight: 1.7,
        paddingTop: '3px',
      }}>
        {text}
      </span>
    </li>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AboutPage() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [width, setWidth]               = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [hoveredCard, setHoveredCard]   = useState<string | null>(null);
  const [hoveredStat, setHoveredStat]   = useState<number | null>(null);
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  const [hoveredTeam, setHoveredTeam]   = useState<number | null>(null);

  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const isMobile = width < 768;

  // Scroll-reveal refs
  const [visionRef,   visionIn]   = useReveal();
  const [cardsRef,    cardsIn]    = useReveal();
  const [statsRef,    statsIn]    = useReveal();
  const [missionRef,  missionIn]  = useReveal();
  const [objRef,      objIn]      = useReveal();
  const [valuesRef,   valuesIn]   = useReveal();
  const [valGridRef,  valGridIn]  = useReveal();
  const [teamRef,     teamIn]     = useReveal();
  const [teamGridRef, teamGridIn] = useReveal();
  const [ctaRef,      ctaIn]      = useReveal();

  const sectionStyle: React.CSSProperties = {
    maxWidth: '1280px',
    margin:   '0 auto',
    padding:  isMobile ? '60px 20px' : '80px 48px',
  };

  // ── Glassmorphism card base ───────────────────────────────────────────────
  const glassCard = (hovered: boolean): React.CSSProperties => ({
    backdropFilter:       'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    background:           hovered
      ? (isDark ? 'rgba(18,24,42,0.80)' : 'rgba(255,255,255,0.90)')
      : (isDark ? 'rgba(13,18,32,0.60)' : 'rgba(255,255,255,0.72)'),
    border:               `1px solid ${hovered
      ? (isDark ? 'rgba(124,111,247,0.45)' : 'rgba(79,63,245,0.28)')
      : (isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)')}`,
    borderRadius:         '20px',
    padding:              '36px',
    transition:           'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease, background 0.28s ease',
    transform:            hovered ? 'translateY(-7px)' : 'translateY(0)',
    boxShadow:            hovered
      ? (isDark
          ? '0 0 0 1px rgba(124,111,247,0.15), 0 24px 56px rgba(79,63,245,0.22)'
          : '0 0 0 1px rgba(79,63,245,0.10), 0 20px 50px rgba(79,63,245,0.12)')
      : (isDark
          ? '0 4px 24px rgba(0,0,0,0.30)'
          : '0 2px 16px rgba(0,0,0,0.06)'),
    position:             'relative',
    overflow:             'hidden',
  });

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = [
    { num: '12+',   label: 'Comprehensive Units'  },
    { num: '7',     label: 'Specialized Books'     },
    { num: '1000+', label: 'Learning Materials'    },
    { num: '24/7',  label: 'Global Access'         },
  ];

  // ── Values ────────────────────────────────────────────────────────────────
  const values: Array<{ icon: React.ReactNode; title: string; body: string }> = [
    { icon: <IcoGradCap />,  title: 'Educational Excellence', body: 'Maintaining rigorous academic standards while ensuring practical, real-world applicability in all materials.'                        },
    { icon: <IcoGlobe20 />,  title: 'Global Perspective',     body: 'Developing resources that respect and incorporate international sports cultures and communication styles.'                            },
    { icon: <IcoUsers20 />,  title: 'Inclusivity',            body: 'Creating accessible materials for diverse learners across different sports disciplines and proficiency levels.'                       },
    { icon: <IcoBulb20 />,   title: 'Innovation',             body: 'Continuously evolving teaching methods and integrating cutting-edge educational technology.'                                         },
    { icon: <IcoLink20 />,   title: 'Collaboration',          body: 'Partnering with sports organizations, educational institutions, and language experts worldwide.'                                     },
    { icon: <IcoTrend20 />,  title: 'Impact-Driven',          body: 'Focusing on measurable outcomes that enhance professional communication and career advancement.'                                     },
  ];

  // ── Team ──────────────────────────────────────────────────────────────────
  const team = [
    {
      initials: 'NT', name: 'Nikoloz Todua',
      role: 'Lead Developer & Platform Architect',
      bio: "Specializing in educational technology and web development, Nikoloz creates the innovative digital infrastructure powering START's global learning platform.",
      linkedin: 'https://www.linkedin.com/in/nikoloz-todua-210b9b370/',
      email: 'n.todua@sportenglish.com',
    },
    {
      initials: 'ESP', name: 'ESP Specialists',
      role: 'Curriculum Development Team',
      bio: 'Our team of ESP experts includes linguists, sports professionals, and educators who collaborate to create context-specific language materials for various sports disciplines.',
    },
    {
      initials: 'INT', name: 'International Partners',
      role: 'Global Sports Organizations',
      bio: 'We collaborate with sports federations, educational institutions, and professional organizations worldwide to ensure our resources meet real-world needs.',
    },
  ];

  // ── Sport badge items ──────────────────────────────────────────────────────
  const sportBadges = [
    { Icon: SportSoccer,  label: 'Football',  rot: '-4deg', delay: '0s'    },
    { Icon: SportRun,     label: 'Athletics', rot: '3deg',  delay: '0.15s' },
    { Icon: SportSwim,    label: 'Swimming',  rot: '-2deg', delay: '0.3s'  },
    { Icon: SportTrophy,  label: 'Champions', rot: '4deg',  delay: '0.45s' },
    { Icon: SportWhistle, label: 'Coaching',  rot: '-3deg', delay: '0.6s'  },
  ];

  // ── CTA card icons ────────────────────────────────────────────────────────
  const IcoRocket24 = () => <svg width="22" height="22" viewBox="0 0 24 24" {...SI}><path d="M4.5 16.5c-1.5 1.5-1.5 3.5 0 5s3.5 1.5 5 0l10-10c3-3 3-8 0-10-2-1-5-1-7 2z"/><circle cx="16" cy="8" r="1.5" fill="currentColor" stroke="none"/></svg>;
  const IcoGlobe24  = () => <svg width="22" height="22" viewBox="0 0 24 24" {...SI}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;

  // Value icon colors (cycling)
  const valueAccents = [C.accent, C.accent2, PURPLE, C.accent, C.accent2, PURPLE];

  return (
    <div style={{
      minHeight:  '100vh',
      background: C.bg,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      transition: 'background 0.3s ease',
      position:   'relative',
    }}>

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <div style={{
        background:   isDark
          ? 'linear-gradient(160deg, rgba(8,12,20,1) 0%, rgba(16,24,52,1) 60%, rgba(24,16,48,1) 100%)'
          : 'linear-gradient(160deg, rgba(248,250,252,1) 0%, rgba(232,242,255,1) 60%, rgba(238,234,255,1) 100%)',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
        padding:      isMobile ? '60px 20px 56px' : '80px 48px 72px',
        textAlign:    'center',
        position:     'relative',
        overflow:     'hidden',
        minHeight:    isMobile ? '340px' : '380px',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
      }}>
        {/* Particle field */}
        <AboutParticleField isDark={isDark} />

        {/* Animated energy curves */}
        <HeaderEnergyCurve C={C} />

        {/* Mesh blobs */}
        <MeshBlob
          color={isDark
            ? `radial-gradient(circle, rgba(79,63,245,0.35), transparent)`
            : `radial-gradient(circle, rgba(79,63,245,0.12), transparent)`}
          size="480px"
          pos={{ top: '-120px', left: '-80px' }}
          anim="abMeshFloat1 18s ease-in-out infinite"
          opacity={0.8}
        />
        <MeshBlob
          color={isDark
            ? `radial-gradient(circle, rgba(79,140,247,0.28), transparent)`
            : `radial-gradient(circle, rgba(79,140,247,0.10), transparent)`}
          size="380px"
          pos={{ bottom: '-100px', right: '-60px' }}
          anim="abMeshFloat2 22s ease-in-out infinite"
          opacity={0.7}
        />
        <MeshBlob
          color={isDark
            ? `radial-gradient(circle, rgba(168,133,247,0.22), transparent)`
            : `radial-gradient(circle, rgba(168,133,247,0.08), transparent)`}
          size="260px"
          pos={{ top: '20%', right: '25%' }}
          anim="abMeshFloat3 16s ease-in-out 4s infinite"
          opacity={0.6}
        />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '720px', margin: '0 auto', width: '100%' }}>
          {/* Badge */}
          <div style={{ animation: 'abRevealUp 0.6s ease 0.1s both' }}>
            <SectionBadge icon={<IcoTarget />} label="Our Vision" C={C} isDark={isDark} />
          </div>

          {/* Headline */}
          <h1
            style={{
              fontSize:             isMobile ? '2.5rem' : '3.4rem',
              fontWeight:           900,
              lineHeight:           1.12,
              margin:               '0 0 20px',
              letterSpacing:        '-0.03em',
              background:           `linear-gradient(135deg, ${C.text} 0%, ${C.accent} 55%, ${C.accent2} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
              animation:            'abRevealUp 0.65s ease 0.2s both',
            }}
          >
            About <span style={{ WebkitTextFillColor: C.accent }}>START</span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize:  isMobile ? '1.05rem' : '1.12rem',
            color:     C.muted,
            maxWidth:  '580px',
            margin:    '0 auto 32px',
            lineHeight:1.78,
            animation: 'abRevealUp 0.65s ease 0.32s both',
          }}>
            Empowering sports professionals worldwide through specialized English communication training
          </p>

          {/* Sport icon badges */}
          <div style={{
            display:        'flex',
            justifyContent: 'center',
            gap:            isMobile ? '8px' : '12px',
            flexWrap:       'wrap',
            animation:      'abRevealUp 0.65s ease 0.44s both',
          }}>
            {sportBadges.map(({ Icon, label, rot, delay }) => (
              <div
                key={label}
                title={label}
                style={{
                  width:                '48px',
                  height:               '48px',
                  borderRadius:         '50%',
                  backdropFilter:       'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  background:           isDark ? 'rgba(255,255,255,0.07)' : 'rgba(79,140,247,0.08)',
                  border:               `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(79,140,247,0.20)'}`,
                  display:              'inline-flex',
                  alignItems:           'center',
                  justifyContent:       'center',
                  color:                C.accent,
                  boxShadow:            isDark ? `0 4px 16px rgba(79,63,245,0.20)` : `0 2px 8px rgba(79,140,247,0.12)`,
                  ['--badge-rot' as never]: rot,
                  animationDelay:       delay,
                  animation:            `abBadgeFloat 6s ease-in-out ${delay} infinite`,
                } as React.CSSProperties}
              >
                <Icon />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Section 1: Vision ─────────────────────────────────────────────── */}
      <section style={sectionStyle}>
        <div ref={visionRef}>
          <div style={revealStyle(visionIn, 0)}>
            <SectionBadge icon={<IcoRocket />} label="Why We Exist" C={C} isDark={isDark} />
          </div>
          <div style={revealStyle(visionIn, 60)}>
            <GradientTitle C={C}>Pioneering <span style={{ color: C.accent }}>ESP in Sports</span></GradientTitle>
          </div>
          <p style={{ fontSize: '1.05rem', color: C.muted, maxWidth: '640px', lineHeight: 1.78, margin: '0 0 44px', ...revealStyle(visionIn, 120) }}>
            Transforming sports education through specialized language resources that bridge cultures and enhance professional communication worldwide.
          </p>
        </div>

        {/* Vision cards */}
        <div
          ref={cardsRef}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap:                 '24px',
            marginBottom:        '44px',
          }}
        >
          {[
            {
              id: 'why', Icon: IcoRocket24, title: 'Why We Exist',
              paras: [
                'START was founded to address a critical gap: the need for context-specific English resources that go beyond general language learning.',
                'Sports professionals require specialized vocabulary, communication strategies, and cultural understanding unique to their field.',
              ],
            },
            {
              id: 'global', Icon: IcoGlobe24, title: 'Global Reach',
              paras: [
                'By developing comprehensive ESP curricula for diverse sports professions, we create accessible digital learning platforms with truly global reach.',
                'Our resources support professional development through language mastery while fostering international collaboration.',
              ],
            },
          ].map((card, ci) => (
            <div
              key={card.id}
              style={{ ...glassCard(hoveredCard === card.id), ...revealStyle(cardsIn, ci * 120) }}
              onMouseEnter={() => setHoveredCard(card.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Animated top accent bar */}
              <div style={{
                position:     'absolute',
                top:          0,
                left:         0,
                right:        0,
                height:       '2px',
                background:   `linear-gradient(90deg, ${C.accent}, ${C.accent2}, ${PURPLE})`,
                opacity:      hoveredCard === card.id ? 1 : 0,
                transition:   'opacity 0.28s ease',
                borderRadius: '20px 20px 0 0',
              }} />

              {/* Subtle inner glow on hover */}
              <div style={{
                position:   'absolute',
                top:        0,
                left:       0,
                right:      0,
                height:     '80px',
                background: hoveredCard === card.id
                  ? (isDark
                      ? `linear-gradient(180deg, rgba(79,63,245,0.08), transparent)`
                      : `linear-gradient(180deg, rgba(79,63,245,0.04), transparent)`)
                  : 'transparent',
                transition: 'background 0.28s ease',
                pointerEvents: 'none',
                borderRadius: '20px 20px 0 0',
              }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', position: 'relative' }}>
                <div style={{
                  width:        '48px',
                  height:       '48px',
                  borderRadius: '14px',
                  flexShrink:   0,
                  background:   `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                  color:        '#fff',
                  boxShadow:    `0 6px 20px ${isDark ? 'rgba(79,63,245,0.40)' : 'rgba(79,63,245,0.22)'}`,
                  transition:   'box-shadow 0.28s ease',
                }}>
                  <card.Icon />
                </div>
                <h3 style={{ margin: 0, fontSize: '1.22rem', fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>{card.title}</h3>
              </div>

              {card.paras.map((p, i) => (
                <p key={i} style={{
                  color:       C.muted,
                  lineHeight:  1.78,
                  margin:      i < card.paras.length - 1 ? '0 0 12px' : 0,
                  fontSize:    '0.97rem',
                  position:    'relative',
                }}>
                  {p}
                </p>
              ))}
            </div>
          ))}
        </div>

        {/* Stats strip */}
        <div
          ref={statsRef}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap:                 '16px',
          }}
        >
          {stats.map((s, i) => (
            <div
              key={i}
              style={{
                textAlign:            'center',
                backdropFilter:       'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                background:           hoveredStat === i
                  ? (isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.08)')
                  : (isDark ? 'rgba(79,140,247,0.07)' : 'rgba(79,140,247,0.04)'),
                border:               `1px solid ${hoveredStat === i
                  ? (isDark ? 'rgba(79,140,247,0.40)' : 'rgba(79,140,247,0.28)')
                  : (isDark ? 'rgba(79,140,247,0.18)' : 'rgba(79,140,247,0.14)')}`,
                borderRadius:         '18px',
                padding:              '32px 16px',
                transition:           'transform 0.28s ease, border-color 0.28s ease, background 0.28s ease, box-shadow 0.28s ease',
                transform:            hoveredStat === i ? 'translateY(-6px)' : 'translateY(0)',
                boxShadow:            hoveredStat === i
                  ? `0 0 0 1px rgba(79,140,247,0.12), 0 16px 40px rgba(79,63,245,0.16)`
                  : 'none',
                cursor:               'default',
                ...revealStyle(statsIn, i * 80),
              }}
              onMouseEnter={() => setHoveredStat(i)}
              onMouseLeave={() => setHoveredStat(null)}
            >
              <div style={{
                fontSize:             hoveredStat === i ? '2.8rem' : '2.6rem',
                fontWeight:           900,
                lineHeight:           1,
                marginBottom:         '8px',
                letterSpacing:        '-0.03em',
                background:           `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor:  'transparent',
                backgroundClip:       'text',
                transition:           'font-size 0.28s ease',
                filter:               hoveredStat === i
                  ? `drop-shadow(0 0 12px ${isDark ? 'rgba(79,140,247,0.5)' : 'rgba(79,140,247,0.3)'})`
                  : 'none',
              }}>
                {s.num}
              </div>
              <div style={{
                fontSize:   '0.87rem',
                color:      C.muted,
                fontWeight: 600,
                letterSpacing: '0.01em',
              }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section divider */}
      <div style={{
        maxWidth: '1280px', margin: '0 auto', padding: '0 48px',
      }}>
        <div style={{
          height:     '1px',
          background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}, transparent)`,
        }} />
      </div>

      {/* ── Section 2: Mission ────────────────────────────────────────────── */}
      <section style={{ ...sectionStyle }}>
        <div ref={missionRef}>
          <div style={revealStyle(missionIn, 0)}>
            <SectionBadge icon={<IcoFlag />} label="Our Mission" C={C} isDark={isDark} />
          </div>
          <div style={revealStyle(missionIn, 60)}>
            <GradientTitle C={C}>Core <span>Objectives</span></GradientTitle>
          </div>
          <p style={{ fontSize: '1.05rem', color: C.muted, maxWidth: '640px', lineHeight: 1.78, margin: '0 0 36px', ...revealStyle(missionIn, 120) }}>
            To revolutionize sports education by providing comprehensive, context-specific English language resources that enhance professional communication and career opportunities.
          </p>
        </div>

        <div
          ref={objRef}
          style={{
            backdropFilter:       'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            background:           isDark ? 'rgba(13,18,32,0.58)' : 'rgba(255,255,255,0.70)',
            border:               `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
            borderRadius:         '22px',
            padding:              isMobile ? '24px 16px' : '32px 28px',
            boxShadow:            isDark
              ? '0 8px 40px rgba(0,0,0,0.28)'
              : '0 4px 20px rgba(0,0,0,0.06)',
            position:             'relative',
            overflow:             'hidden',
          }}
        >
          {/* Subtle corner glow */}
          <div style={{
            position: 'absolute', top: '-40px', right: '-40px',
            width: '180px', height: '180px',
            borderRadius: '50%',
            background: isDark
              ? `radial-gradient(circle, rgba(79,63,245,0.12), transparent)`
              : `radial-gradient(circle, rgba(79,63,245,0.06), transparent)`,
            pointerEvents: 'none',
          }} />

          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[
              'Develop specialized ESP curricula for athletes, coaches, fitness professionals, and rehabilitation specialists',
              'Create accessible digital learning platforms reaching sports professionals in every corner of the globe',
              'Foster international collaboration between sports organizations, educational institutions, and language experts',
              'Support career advancement through language mastery and cultural understanding',
              'Bridge communication gaps that hinder international sports cooperation and knowledge sharing',
            ].map((obj, i) => (
              <MissionRow
                key={i}
                idx={i}
                text={obj}
                C={C}
                isDark={isDark}
                visible={objIn}
                delay={i * 90}
              />
            ))}
          </ul>
        </div>
      </section>

      {/* Section divider */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 48px' }}>
        <div style={{
          height:     '1px',
          background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}, transparent)`,
        }} />
      </div>

      {/* ── Section 3: Values ─────────────────────────────────────────────── */}
      <section style={{ ...sectionStyle }}>
        <div ref={valuesRef}>
          <div style={revealStyle(valuesIn, 0)}>
            <SectionBadge icon={<IcoHeart />} label="Our Values" C={C} isDark={isDark} />
          </div>
          <div style={revealStyle(valuesIn, 60)}>
            <GradientTitle C={C}>Guiding <span>Principles</span></GradientTitle>
          </div>
          <p style={{ fontSize: '1.05rem', color: C.muted, maxWidth: '640px', lineHeight: 1.78, margin: '0 0 36px', ...revealStyle(valuesIn, 120) }}>
            The principles that guide every aspect of our work and define our commitment to excellence in sports education.
          </p>
        </div>

        <div
          ref={valGridRef}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap:                 '18px',
          }}
        >
          {values.map((v, i) => (
            <div
              key={i}
              style={{
                backdropFilter:       'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                background:           hoveredValue === i
                  ? (isDark ? 'rgba(18,24,42,0.78)' : 'rgba(255,255,255,0.90)')
                  : (isDark ? 'rgba(13,18,32,0.55)' : 'rgba(255,255,255,0.68)'),
                border:               `1px solid ${hoveredValue === i
                  ? (isDark ? `rgba(${i % 2 === 0 ? '79,140,247' : '124,111,247'},0.42)` : `rgba(79,63,245,0.26)`)
                  : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)')}`,
                borderRadius:         '16px',
                padding:              '26px 24px',
                transition:           'transform 0.28s ease, border-color 0.28s ease, background 0.28s ease, box-shadow 0.28s ease',
                transform:            hoveredValue === i ? 'translateY(-6px)' : 'translateY(0)',
                boxShadow:            hoveredValue === i
                  ? `0 0 0 1px rgba(79,140,247,0.10), 0 16px 42px rgba(79,63,245,0.16)`
                  : (isDark ? '0 2px 16px rgba(0,0,0,0.22)' : '0 2px 10px rgba(0,0,0,0.05)'),
                cursor:               'default',
                position:             'relative',
                overflow:             'hidden',
                ...revealStyle(valGridIn, i * 70),
              }}
              onMouseEnter={() => setHoveredValue(i)}
              onMouseLeave={() => setHoveredValue(null)}
            >
              {/* Gradient top accent */}
              <div style={{
                position:   'absolute',
                top:        0, left: 0, right: 0,
                height:     '2px',
                background: `linear-gradient(90deg, ${valueAccents[i]}, ${C.accent2})`,
                opacity:    hoveredValue === i ? 1 : 0,
                transition: 'opacity 0.28s ease',
                borderRadius: '16px 16px 0 0',
              }} />

              <div style={{
                display:        'flex',
                alignItems:     'center',
                gap:            '12px',
                marginBottom:   '12px',
              }}>
                <div style={{
                  width:          '40px',
                  height:         '40px',
                  borderRadius:   '11px',
                  background:     hoveredValue === i
                    ? `linear-gradient(135deg, ${valueAccents[i]}, ${C.accent2})`
                    : (isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.10)'),
                  border:         `1px solid ${isDark ? 'rgba(79,140,247,0.25)' : 'rgba(79,140,247,0.18)'}`,
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  color:          hoveredValue === i ? '#fff' : valueAccents[i],
                  flexShrink:     0,
                  transition:     'background 0.28s ease, color 0.28s ease, box-shadow 0.28s ease',
                  boxShadow:      hoveredValue === i
                    ? `0 4px 16px rgba(79,63,245,0.28)`
                    : 'none',
                }}>
                  {v.icon}
                </div>
                <h4 style={{
                  margin:        0,
                  fontSize:      '1.02rem',
                  fontWeight:    800,
                  color:         hoveredValue === i ? C.accent : C.text,
                  letterSpacing: '-0.005em',
                  transition:    'color 0.22s ease',
                }}>
                  {v.title}
                </h4>
              </div>
              <p style={{ color: C.muted, lineHeight: 1.68, margin: 0, fontSize: '0.92rem' }}>{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section divider */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 48px' }}>
        <div style={{
          height:     '1px',
          background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}, transparent)`,
        }} />
      </div>

      {/* ── Section 4: Team ───────────────────────────────────────────────── */}
      <section style={{ ...sectionStyle }}>
        <div ref={teamRef}>
          <div style={revealStyle(teamIn, 0)}>
            <SectionBadge icon={<IcoUsers />} label="The Team" C={C} isDark={isDark} />
          </div>
          <div style={revealStyle(teamIn, 60)}>
            <GradientTitle C={C}>Who's <span>Behind START</span></GradientTitle>
          </div>
          <p style={{ fontSize: '1.05rem', color: C.muted, maxWidth: '640px', lineHeight: 1.78, margin: '0 0 36px', ...revealStyle(teamIn, 120) }}>
            Experts in sports education, linguistics, and digital learning from around the world.
          </p>
        </div>

        <div
          ref={teamGridRef}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap:                 '24px',
          }}
        >
          {team.map((member, i) => (
            <div
              key={i}
              style={{
                backdropFilter:       'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                background:           hoveredTeam === i
                  ? (isDark ? 'rgba(14,20,38,0.88)' : 'rgba(255,255,255,0.92)')
                  : (isDark ? 'rgba(10,14,28,0.65)' : 'rgba(255,255,255,0.72)'),
                border:               `1px solid ${hoveredTeam === i
                  ? (isDark ? 'rgba(124,111,247,0.40)' : 'rgba(79,63,245,0.24)')
                  : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)')}`,
                borderRadius:         '22px',
                overflow:             'hidden',
                transition:           'transform 0.30s ease, box-shadow 0.30s ease, border-color 0.30s ease',
                transform:            hoveredTeam === i ? 'translateY(-8px)' : 'translateY(0)',
                boxShadow:            hoveredTeam === i
                  ? (isDark
                      ? '0 0 0 1px rgba(124,111,247,0.15), 0 28px 64px rgba(79,63,245,0.24)'
                      : '0 0 0 1px rgba(79,63,245,0.08), 0 24px 56px rgba(79,63,245,0.14)')
                  : (isDark ? '0 4px 24px rgba(0,0,0,0.30)' : '0 2px 12px rgba(0,0,0,0.06)'),
                cursor:               'default',
                ...revealStyle(teamGridIn, i * 130),
              }}
              onMouseEnter={() => setHoveredTeam(i)}
              onMouseLeave={() => setHoveredTeam(null)}
            >
              {/* Card header with gradient + orbit ring avatar */}
              <div style={{
                background: isDark
                  ? `linear-gradient(145deg, rgba(20,16,48,0.95) 0%, rgba(16,28,72,0.95) 60%, rgba(10,14,36,0.95) 100%)`
                  : `linear-gradient(145deg, #003366 0%, ${C.accent} 100%)`,
                padding:    '40px 24px 32px',
                textAlign:  'center',
                color:      '#fff',
                position:   'relative',
                overflow:   'hidden',
              }}>
                {/* Header mesh glow */}
                <div style={{
                  position:   'absolute', top: '-30px', left: '-30px',
                  width:      '180px', height: '180px',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, rgba(79,63,245,0.25), transparent)`,
                  filter:     'blur(30px)',
                  pointerEvents: 'none',
                  animation:  `abMeshFloat1 ${14 + i * 2}s ease-in-out ${i * 1.5}s infinite`,
                }} />
                <div style={{
                  position:   'absolute', bottom: '-20px', right: '-20px',
                  width:      '120px', height: '120px',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, rgba(79,140,247,0.20), transparent)`,
                  filter:     'blur(20px)',
                  pointerEvents: 'none',
                }} />

                <InitialsOrbit initials={member.initials} C={C} isDark={isDark} />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: '0 0 5px', letterSpacing: '-0.01em' }}>{member.name}</h3>
                <span style={{
                  fontSize:      '0.83rem',
                  opacity:       0.82,
                  background:    'rgba(255,255,255,0.10)',
                  padding:       '3px 12px',
                  borderRadius:  '50px',
                  border:        '1px solid rgba(255,255,255,0.18)',
                  display:       'inline-block',
                }}>
                  {member.role}
                </span>
              </div>

              {/* Card body */}
              <div style={{ padding: '26px' }}>
                <p style={{
                  color:       C.muted,
                  lineHeight:  1.72,
                  fontSize:    '0.93rem',
                  margin:      (member.linkedin || member.email) ? '0 0 18px' : '0',
                }}>
                  {member.bio}
                </p>
                {(member.linkedin || member.email) && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        style={{
                          width:          '36px',
                          height:         '36px',
                          borderRadius:   '50%',
                          background:     isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.08)',
                          border:         `1px solid ${isDark ? 'rgba(79,140,247,0.28)' : 'rgba(79,140,247,0.20)'}`,
                          display:        'flex',
                          alignItems:     'center',
                          justifyContent: 'center',
                          color:          C.accent,
                          textDecoration: 'none',
                          transition:     'background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease',
                        }}
                        onMouseEnter={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.background  = C.accent;
                          el.style.color       = '#fff';
                          el.style.boxShadow   = `0 4px 16px ${isDark ? 'rgba(79,63,245,0.50)' : 'rgba(79,63,245,0.28)'}`;
                          el.style.transform   = 'translateY(-2px)';
                        }}
                        onMouseLeave={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.background  = isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.08)';
                          el.style.color       = C.accent;
                          el.style.boxShadow   = 'none';
                          el.style.transform   = 'translateY(0)';
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      </a>
                    )}
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        aria-label="Email"
                        style={{
                          width:          '36px',
                          height:         '36px',
                          borderRadius:   '50%',
                          background:     isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.08)',
                          border:         `1px solid ${isDark ? 'rgba(79,140,247,0.28)' : 'rgba(79,140,247,0.20)'}`,
                          display:        'flex',
                          alignItems:     'center',
                          justifyContent: 'center',
                          color:          C.accent,
                          textDecoration: 'none',
                          transition:     'background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease',
                        }}
                        onMouseEnter={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.background  = C.accent;
                          el.style.color       = '#fff';
                          el.style.boxShadow   = `0 4px 16px ${isDark ? 'rgba(79,63,245,0.50)' : 'rgba(79,63,245,0.28)'}`;
                          el.style.transform   = 'translateY(-2px)';
                        }}
                        onMouseLeave={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.background  = isDark ? 'rgba(79,140,247,0.12)' : 'rgba(79,140,247,0.08)';
                          el.style.color       = C.accent;
                          el.style.boxShadow   = 'none';
                          el.style.transform   = 'translateY(0)';
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section 5: CTA Banner ─────────────────────────────────────────── */}
      <section style={{ ...sectionStyle, paddingTop: 0 }}>
        <div ref={ctaRef} style={revealStyle(ctaIn, 0)}>
          <div style={{
            background:   isDark
              ? 'linear-gradient(140deg, rgba(20,16,52,0.95) 0%, rgba(16,32,80,0.95) 50%, rgba(8,20,48,0.95) 100%)'
              : 'linear-gradient(140deg, #003366 0%, #1e4fd9 50%, #4f3ff5 100%)',
            backdropFilter:       isDark ? 'blur(20px)' : undefined,
            WebkitBackdropFilter: isDark ? 'blur(20px)' : undefined,
            border:               isDark ? '1px solid rgba(124,111,247,0.20)' : 'none',
            borderRadius:         '26px',
            padding:              isMobile ? '52px 28px' : '68px 56px',
            textAlign:            'center',
            color:                '#fff',
            position:             'relative',
            overflow:             'hidden',
          }}>
            {/* CTA mesh blobs */}
            <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <div style={{
                position: 'absolute', top: '-60px', left: '-40px',
                width: '300px', height: '300px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.09) 0%, transparent 70%)',
                filter: 'blur(40px)',
                animation: 'abMeshFloat1 16s ease-in-out infinite',
              }} />
              <div style={{
                position: 'absolute', bottom: '-80px', right: '-40px',
                width: '280px', height: '280px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(79,140,247,0.18) 0%, transparent 70%)',
                filter: 'blur(50px)',
                animation: 'abMeshFloat2 20s ease-in-out 2s infinite',
              }} />
              <div style={{
                position: 'absolute', top: '30%', right: '15%',
                width: '160px', height: '160px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                filter: 'blur(24px)',
              }} />
              {/* Grid dot overlay */}
              <div style={{
                position:  'absolute',
                inset:     0,
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
                opacity:   0.4,
              }} />
            </div>

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Small badge */}
              <div style={{
                display:       'inline-flex',
                alignItems:    'center',
                gap:           '6px',
                background:    'rgba(255,255,255,0.12)',
                border:        '1px solid rgba(255,255,255,0.22)',
                borderRadius:  '50px',
                padding:       '5px 14px',
                fontSize:      '0.73rem',
                fontWeight:    700,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color:         'rgba(255,255,255,0.90)',
                marginBottom:  '20px',
                backdropFilter: 'blur(8px)',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                Join Our Mission
              </div>

              <h2 style={{
                fontSize:      isMobile ? '1.75rem' : '2.25rem',
                fontWeight:    900,
                margin:        '0 0 18px',
                letterSpacing: '-0.025em',
                lineHeight:    1.2,
              }}>
                Join Our Mission
              </h2>
              <p style={{
                fontSize:   '1.05rem',
                opacity:    0.88,
                maxWidth:   '620px',
                margin:     '0 auto 40px',
                lineHeight: 1.75,
              }}>
                Whether you're a sports professional, educator, or organization — we invite you to be part of our global community.
              </p>

              <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* Primary CTA — shimmer */}
                <a
                  href="/books"
                  style={{
                    display:            'inline-flex',
                    alignItems:         'center',
                    gap:                '8px',
                    padding:            '14px 30px',
                    borderRadius:       '50px',
                    background:         'linear-gradient(90deg, #fff 0%, rgba(230,236,255,1) 40%, #fff 70%, rgba(230,236,255,1) 100%)',
                    backgroundSize:     '200% auto',
                    color:              C.accent,
                    fontWeight:         800,
                    fontSize:           '0.94rem',
                    textDecoration:     'none',
                    letterSpacing:      '-0.005em',
                    transition:         'transform 0.22s ease, box-shadow 0.22s ease',
                    animation:          'abShimmer 3.5s linear infinite',
                    boxShadow:          '0 4px 20px rgba(0,0,0,0.22)',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.transform  = 'translateY(-3px)';
                    el.style.boxShadow  = '0 12px 36px rgba(0,0,0,0.30)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.transform  = 'translateY(0)';
                    el.style.boxShadow  = '0 4px 20px rgba(0,0,0,0.22)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                  Explore Materials
                </a>

                {/* Secondary CTA */}
                <a
                  href="/contact"
                  style={{
                    display:        'inline-flex',
                    alignItems:     'center',
                    gap:            '8px',
                    padding:        '14px 30px',
                    borderRadius:   '50px',
                    background:     'rgba(255,255,255,0.08)',
                    color:          '#fff',
                    border:         '1.5px solid rgba(255,255,255,0.52)',
                    fontWeight:     700,
                    fontSize:       '0.94rem',
                    textDecoration: 'none',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    transition:     'background 0.22s ease, transform 0.22s ease, border-color 0.22s ease',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background   = 'rgba(255,255,255,0.16)';
                    el.style.borderColor  = 'rgba(255,255,255,0.75)';
                    el.style.transform    = 'translateY(-3px)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background   = 'rgba(255,255,255,0.08)';
                    el.style.borderColor  = 'rgba(255,255,255,0.52)';
                    el.style.transform    = 'translateY(0)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}